/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { ensureKetchStub, isKetchEnabled, ketchConsentToTrackingConsent, type KetchConsent } from '@/lib/ketch';

/**
 * Subscribes to Ketch `consent` events and mirrors them onto SFCC tracking consent
 * (SLAS `dnt` / `dw_dnt`) so Einstein, Active Data, and Data Cloud stay gated.
 *
 * Renders nothing — the Smart Tag itself paints the banner / preference center.
 */
export function KetchConsentBridge() {
    const config = useConfig();
    const { setTrackingConsent, trackingConsent, isTrackingConsentEnabled } = useTrackingConsent();
    const lastSyncedRef = useRef<typeof trackingConsent>(undefined);

    const ketchConfig = config.ketch;
    const trackingPurposes = ketchConfig?.trackingPurposes;
    const enabled = isKetchEnabled(ketchConfig);

    useEffect(() => {
        if (!enabled || !isTrackingConsentEnabled) {
            return;
        }

        ensureKetchStub();

        const onConsent = (consent: KetchConsent | Record<string, boolean>) => {
            const mapped = ketchConsentToTrackingConsent(consent, trackingPurposes);
            if (mapped === lastSyncedRef.current || mapped === trackingConsent) {
                lastSyncedRef.current = mapped;
                return;
            }
            lastSyncedRef.current = mapped;
            void setTrackingConsent(mapped);
        };

        window.ketch?.('on', 'consent', onConsent);

        return () => {
            window.ketch?.('off', 'consent', onConsent);
        };
    }, [enabled, isTrackingConsentEnabled, setTrackingConsent, trackingConsent, trackingPurposes]);

    return null;
}
